import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings, useHomepageSections } from '@/hooks/useSiteData';
import { useParallax } from '@/hooks/useParallax';
import { TypewriterText } from '@/components/ui/TypewriterText';
import massaggiIcon from '@/assets/massaggi-icon.webp';
import tantraIcon from '@/assets/tantra-icon.webp';
import corsiIcon from '@/assets/corsi-icon.webp';
import eventiIcon from '@/assets/eventi-icon.webp';
import heroBg640 from '@/assets/hero-background-640.webp';
import heroBg1280 from '@/assets/hero-background-1280.webp';
import heroBg1920 from '@/assets/hero-background-1920.webp';

const iconMap: Record<string, string> = {
  massaggi: massaggiIcon,
  tantra: tantraIcon,
  corsi: corsiIcon,
  eventi: eventiIcon,
};

export function VideoHero() {
  const { data: settings } = useSiteSettings();
  const { data: sections } = useHomepageSections();
  const parallaxOffset = useParallax(0.4);
  const [titleComplete, setTitleComplete] = useState(false);
  
  const heroSection = sections?.find(s => s.section_key === 'hero');
  const title = heroSection?.title || settings?.site_name || 'Karmaio';
  const subtitle = heroSection?.subtitle || settings?.tagline || 'Benessere Olistico';

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      {/* Parallax Background with Ken Burns effect — responsive <img> for srcset */}
      <img
        src={heroBg1280}
        srcSet={`${heroBg640} 640w, ${heroBg1280} 1280w, ${heroBg1920} 1920w`}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-center animate-slow-zoom grayscale"
        style={{
          transform: `translateY(${parallaxOffset}px) scale(1.1)`,
          willChange: 'transform',
        }}
      />
      
      {/* Overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/80" />
      
      {/* Content - moves slightly slower for depth */}
      <div 
        className="relative z-10 container mx-auto px-4 text-center"
        style={{ 
          transform: `translateY(${parallaxOffset * 0.2}px)`,
          willChange: 'transform',
        }}
      >
        {/* Logo/Title with Typewriter effect */}
        <h1 className="text-5xl md:text-7xl font-bold text-shimmer mb-4 min-h-[1.2em]">
          <TypewriterText 
            text={title} 
            speed={100} 
            delay={500}
            showCursor={!titleComplete}
            onComplete={() => setTitleComplete(true)}
          />
          <span className="sr-only"> — Benessere Olistico, Massaggi e Tantra a Milano</span>
        </h1>

        
        {/* Subtitle appears after title is complete */}
        <div className={`transition-all duration-700 ${titleComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-xl md:text-2xl text-muted-foreground mb-12">
            <TypewriterText 
              text={subtitle} 
              speed={50} 
              delay={titleComplete ? 200 : 10000}
              showCursor={true}
            />
          </p>
        </div>

        {/* Service Icons - stagger in after subtitle */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto transition-all duration-700 delay-500 ${titleComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {['massaggi', 'tantra', 'corsi', 'eventi'].map((key, index) => (
            <Link 
              key={key} 
              to={`/${key}`}
              className="group flex h-full flex-col items-center justify-start gap-3 p-4 transition-all duration-300"
              style={{ 
                transitionDelay: titleComplete ? `${index * 100}ms` : '0ms',
              }}
            >
              <div className="relative h-28 w-28 md:h-36 md:w-36 shrink-0 flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                {/* Glow ring behind icon */}
                <div className="absolute inset-0 rounded-full bg-primary/0 group-hover:bg-primary/10 blur-xl transition-all duration-500 group-hover:scale-150" />
                <img 
                  src={iconMap[key]} 
                  alt={`Icona servizio ${key} Karmaio`} 
                  className="absolute inset-0 h-full w-full object-contain object-bottom drop-shadow-md group-hover:drop-shadow-[0_0_15px_hsl(8_85%_55%/0.5)] transition-all duration-500"
                />

              </div>
              <span className="mt-auto text-sm font-medium uppercase tracking-wider text-foreground group-hover:text-primary transition-all duration-300">
                {key}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
